import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { getAppSource, getShellInfo } from './shellDetection';
import appConfig from 'appConfig';

const connectionString = appConfig.monitoring.appInsightsKey;

const reactPlugin = new ReactPlugin();

const appInsights = new ApplicationInsights({
  config: {
    connectionString,
    extensions: [reactPlugin],
    enableAutoRouteTracking: true, // Tracks route changes
    disableFetchTracking: false, // Tracks fetch API calls
    disableAjaxTracking: false, // Tracks AJAX requests
    disableExceptionTracking: false, // Captures JS errors
    maxBatchInterval: 1000, // Sends telemetry every second
    samplingPercentage: 100, // 100% data collection (adjust in production)
  },
});

// Store user info for telemetry
let currentUserName: string | null = null;
let currentUserEmail: string | null = null;

// Add telemetry initializer to set appSource, appVersion, userName, and userEmail on all telemetry
appInsights.addTelemetryInitializer((envelope) => {
  if (envelope.data) {
    envelope.data['appSource'] = getAppSource();
    envelope.data['appVersion'] = appConfig.version;
    if (currentUserName) {
      envelope.data['userName'] = currentUserName;
    }
    if (currentUserEmail) {
      envelope.data['userEmail'] = currentUserEmail;
    }
  }
  
  // Also add to custom properties for better visibility in Azure Portal
  if (envelope.baseData) {
    if (!envelope.baseData.properties) {
      envelope.baseData.properties = {};
    }
    envelope.baseData.properties['appSource'] = getAppSource();
    envelope.baseData.properties['appVersion'] = appConfig.version;
    if (currentUserName) {
      envelope.baseData.properties['userName'] = currentUserName;
    }
    if (currentUserEmail) {
      envelope.baseData.properties['userEmail'] = currentUserEmail;
    }
  }
  
  return true;
});

appInsights.loadAppInsights();

// Function to set authenticated user context
export const setAppInsightsUser = (userName: string | null, userEmail: string | null, userId?: string) => {
  currentUserName = userName;
  currentUserEmail = userEmail;
  
  // Also set the authenticated user context in Application Insights
  // This helps with user tracking and correlating sessions
  if (userId && userName) {
    appInsights.setAuthenticatedUserContext(userId, userName);
  } else if (userId) {
    appInsights.setAuthenticatedUserContext(userId);
  }
};

// Function to clear user context (on logout)
export const clearAppInsightsUser = () => {
  currentUserName = null;
  currentUserEmail = null;
  appInsights.clearAuthenticatedUserContext();
};

// Make appInsights available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).appInsights = appInsights;
  // Also expose shell detection for debugging
  (window as any).getAppSource = getAppSource;
  (window as any).getShellInfo = getShellInfo;
  (window as any).setAppInsightsUser = setAppInsightsUser;
  (window as any).clearAppInsightsUser = clearAppInsightsUser;
}

export { appInsights, reactPlugin };